import React, { useState } from 'react';
import styled from 'styled-components';

import { HOMEPAGE_CAROUSEL_HEADING_MAX_LENGTH } from 'shared/constants';
import { mediaQueries } from 'shared/styles';
import { InputWithMaxLength } from 'shared/components/inputs-with-max-length';
import { ModalButton, ModalClose, ModalContainer, ModalSecondaryHeader } from 'shared/modals';
import { LocalSection } from '../homepage-form.types';

type HeadingOverrideModalProps = {
  handleAddSection: (newSection: LocalSection) => void;
  menuSection: LocalSection;
  onToggle: () => void;
};

export function HeadingOverrideModal({
  handleAddSection,
  menuSection,
  onToggle,
}: HeadingOverrideModalProps): JSX.Element {
  const [heading, setHeading] = useState(menuSection.headingOverride ?? '');
  const defaultHeading = menuSection.legacyLabel ?? menuSection.label;

  const applyHeading = (headingOverride: string | null): void => {
    handleAddSection({
      ...menuSection,
      headingOverride,
      label: headingOverride ?? defaultHeading,
    });
    onToggle();
  };

  const handleSave = (): void => {
    applyHeading(heading.trim() || null);
  };

  const handleUseDefaultHeading = (): void => {
    applyHeading(null);
  };

  return (
    <ModalContainer
      isOpen
      padding='40px'
      width='415px'
      alignItems='flex-start'
      justifyContent='flex-start'
      textAlign='left'
    >
      <ContentColumn data-testid='heading-modal-content'>
        <ModalSecondaryHeader lowercase mb='8px'>
          Edit carousel heading
        </ModalSecondaryHeader>
        <SectionType>
          Section type: <strong>{defaultHeading}</strong>
        </SectionType>
        <InfoBanner role='note'>
          Products continue to populate using the {defaultHeading} section logic. Only the storefront heading changes.
        </InfoBanner>
        <FieldContainer>
          <HeadingFieldLabel htmlFor='headingOverride'>Custom heading</HeadingFieldLabel>
          <InputWithMaxLength
            label=''
            placeholderText={defaultHeading}
            maxLength={HOMEPAGE_CAROUSEL_HEADING_MAX_LENGTH}
            width={335}
            margin={0}
            id='headingOverride'
            value={heading}
            onChange={(event) => setHeading(event.target.value)}
          />
        </FieldContainer>
        <ButtonRow data-testid='heading-modal-footer'>
          <ModalButton buttonContainerWidth='auto' width='auto' mt='0' px={0} link onClick={handleUseDefaultHeading}>
            Use default heading
          </ModalButton>
          <RightActions data-testid='heading-modal-right-actions'>
            <ModalButton buttonContainerWidth='auto' width='auto' mt='0' inverted onClick={onToggle}>
              Cancel
            </ModalButton>
            <ModalButton buttonContainerWidth='auto' width='auto' mt='0' onClick={handleSave}>
              Save
            </ModalButton>
          </RightActions>
        </ButtonRow>
      </ContentColumn>
      <ModalClose onClick={onToggle} />
    </ModalContainer>
  );
}

const ContentColumn = styled.div`
  box-sizing: border-box;
  width: 100%;
`;

const SectionType = styled.div`
  color: #4f5d68;
  font-size: 13px;
  line-height: 18px;
`;

const InfoBanner = styled.div`
  background: #f3f6f8;
  box-sizing: border-box;
  border: 1px solid #d3d8de;
  border-radius: 6px;
  color: #5b6870;
  font-size: 12px;
  line-height: 18px;
  margin-top: 12px;
  padding: 10px 12px;
  width: 100%;
`;

const FieldContainer = styled.div`
  margin: 24px 0;
  width: 100%;

  > div {
    max-width: 100%;
    width: 100%;
  }
`;

const HeadingFieldLabel = styled.label`
  color: #4f5d68;
  display: block;
  font-size: 13px;
  font-weight: 600;
  margin-bottom: 6px;
`;

const ButtonRow = styled.div`
  align-items: center;
  box-sizing: border-box;
  column-gap: 16px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  row-gap: 12px;
  width: 100%;

  .modal-button-container {
    margin: 0;
    min-width: 0;
  }

  > .modal-button-container {
    justify-self: start;
  }

  @media ${mediaQueries.custom(480)} {
    align-items: stretch;
    grid-template-columns: minmax(0, 1fr);
  }
`;

const RightActions = styled.div`
  align-items: center;
  display: flex;
  flex-shrink: 0;
  flex-wrap: nowrap;
  gap: 12px;
  justify-content: flex-end;

  @media ${mediaQueries.custom(480)} {
    align-self: stretch;
    flex-wrap: wrap;
    width: 100%;
  }
`;
